<template>
  <q-page class="q-pa-md">
    <div class="form-holder q-ma-lg q-pl-xl q-pr-xl">
      <div class="form-container row">
        <!-- Left Column: Form Section -->
        <div class="form-left col-12 col-md-6 q-pr-md q-mb-md">
          <h6 class="q-mt-sm" style="margin-bottom: 1.5em">Ticket</h6>
          <q-form @submit.prevent="handleFormSubmit" @reset="handleReset" ref="baseForm" class="q-gutter-md">
            <div class="form-group">
              <q-input
                filled
                dense
                label="Ticket Number"
                v-model="localFormData.ticket_number"
                ref="ticketNumber"
                @blur="checkDuplicateTicketNumber"
                @keydown.enter.prevent="handleEnterKey('ticketNumber')"
                :hide-bottom-space="true"
              />
              <q-select
                filled dense
                label="Truck"
                v-model="localFormData.truck_id"
                :options="trucks"
                option-value="value"
                option-label="label"
                ref="truckId"
                @update:model-value="onSelectChanged('truckId')"
                emit-value
                map-options
                :hide-bottom-space="true"
              />
              <q-input
                filled
                dense
                label="Gross Wt."
                v-model="localFormData.gross_weight"
                :hide-bottom-space="true"
                ref="grossWeight"
                @keydown.enter.prevent="handleEnterKey('grossWeight')"
              />
              <q-input
                filled
                dense
                label="Tare Wt."
                v-model="localFormData.tare_weight"
                :hide-bottom-space="true"
                ref="tareWeight"
                @keydown.enter.prevent="handleEnterKey('tareWeight')"
              />
            </div>
          </q-form>
          <div class="row q-mt-sm q-gap-md">
            <!-- Buttons -->
            <div class="button-container q-mt-md">
              <q-btn ref="submitBtn" label="Submit/Next" color="primary" unelevated @click="handleFormSubmit" />
              <q-btn label="View All" color="secondary" @click="viewAllJobTickets" flat />
              <q-btn label="Cancel" color="negative" @click="setupNextTicket" flat />
            </div>
          </div>
          <div class=""><br><br></div>
          
          <q-form ref="auxForm" class="q-gutter-md">
            <div class="form-group">
              <q-select
                filled dense
                label="Hauler"
                v-model="localFormData.hauler_id"
                :options="haulers"
                option-value="value"
                option-label="label"
                emit-value
                map-options
                :hide-bottom-space="true"
              />
              <q-select
                filled dense
                label="Bag Pit"
                v-model="localFormData.bag_pit_id"
                :options="bagPitOptions"
                option-value="value"
                option-label="label"
                emit-value
                map-options
                :hide-bottom-space="true"
              />
            </div>
          </q-form>
        </div>

        <!-- Right Column: Job Information Section -->
        <div class="form-right col-12 col-md-6 q-mb-md">
          <h6 class="q-mt-sm" style="margin-bottom: .75em;">Job Information</h6>
          <q-card>
            <q-card-section>
              <div class="row">
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Date:</div>
                    <div>{{ jobData.date }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Producer:</div>
                    <div>{{ jobData.producer_id?.name }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Customer:</div>
                    <div>{{ jobData.customer_id?.name }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Crop:</div>
                    <div>{{ jobData.crop_id?.crop }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Field:</div>
                    <div>{{ jobData.field_id?.name }} Farms</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Bag Pit:</div>
                    <div>{{ jobData.bag_pit_id?.name }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Wet Tons:</div>
                    <div>{{ netWeight }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Moisture:</div>
                    <div>{{ jobData.moisture ? parseFloat(jobData.moisture).toFixed(2) : 0 }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">At Moisture:</div>
                    <div>{{ localFormData.at_moisture ? parseFloat(localFormData.at_moisture).toFixed(2) : 0 }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col q-ma-sm">
                  <div class="info-item bg-dark text-white">
                    <div class="text-weight-bold">Adjusted Weight:</div>
                    <div>{{ adjustedWeight }}</div>
                  </div>
                </div>
                <div class="col q-ma-sm">
                </div>
                <div class="col q-ma-sm">
                </div>
              </div>
            </q-card-section>
          </q-card>
          <h6 class="q-mt-md" style="margin-bottom: 0">Summary Calculations</h6>
          <div class="row q-mt-sm q-gap-md">
            <div class="col-12 col-md-4">
              <q-card flat bordered class="" style="margin: 0 auto; padding: 0">
                <q-card-section style="padding-left: 0; padding-right: 0; padding-top: 0">
                  <div class="bg-dark text-white q-pa-sm">
                    <center><b>Hauling Cost</b></center>
                  </div>
                  <q-markup-table flat>
                    <tbody>
                      <tr>
                        <td class="text-weight-bold">
                          Ton Rate
                        </td>
                        <td>
                          {{ localFormData.haul_rate }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Miles
                        </td>
                        <td>
                          {{ localFormData.haul_mile }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Mile Rate
                        </td>
                        <td>
                          {{ localFormData.haul_mile_rate }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Base
                        </td>
                        <td>
                          {{ baseHaulCost }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Extra Mile
                        </td>
                        <td>
                          {{ extraMileHaulCost }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Total
                        </td>
                        <td>
                          {{ totalHaulCost }}
                        </td>
                      </tr>
                    </tbody>
                  </q-markup-table>
                </q-card-section>
              </q-card>
            </div>
            <div class="col-12 col-md-4">
              <q-card flat bordered class="" style="margin: 0 auto; padding: 0">
                <q-card-section style="padding-left: 0; padding-right: 0; padding-top: 0">
                  <div class="bg-dark text-white q-pa-sm">
                    <center><b>Producer Pay</b></center>
                  </div>
                  <q-markup-table flat>
                    <tbody>
                      <tr>
                        <td class="text-weight-bold">
                          Rate
                        </td>
                        <td>
                          {{ localFormData.producer_rate }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Gross
                        </td>
                        <td>
                          {{ producerGrossPay }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Haul Cost
                        </td>
                        <td>
                          {{ extraMileHaulCost }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Total
                        </td>
                        <td>
                          {{ producerNetPay }}
                        </td>
                      </tr>
                    </tbody>
                  </q-markup-table>
                </q-card-section>
              </q-card>
            </div>
            <div class="col-12 col-md-4">
              <q-card flat bordered class="" style="margin: 0 auto; padding: 0">
                <q-card-section style="padding-left: 0; padding-right: 0; padding-top: 0">
                  <div class="bg-dark text-white q-pa-sm">
                    <center><b>Customer Pay</b></center>
                  </div>
                  <q-markup-table flat>
                    <tbody>
                      <tr>
                        <td class="text-weight-bold">
                          Rate
                        </td>
                        <td>
                          {{ localFormData.customer_rate }}
                        </td>
                      </tr>
                      <tr>
                        <td class="text-weight-bold">
                          Total
                        </td>
                        <td>
                          {{ customerPay }}
                        </td>
                      </tr>
                    </tbody>
                  </q-markup-table>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script>
import axios from 'axios';
import { useRoute } from 'vue-router';

export default {
  setup() {
    const route = useRoute();
    const jobId = route.params.jobId;

    return {
      jobId,
    }
  },
  data() {
    return {
      localFormData: {
        ticket_number: '',
        date: '',
        truck_id: '',
        hauler_id: '',
        customer_id: '',
        producer_id: '',
        gross_weight: null,
        tare_weight: null,
        bag_pit_id: '',
        at_moisture: 0,
        moisture: 0,
        customer_rate: 0,
        producer_rate: 0,
        haul_rate: 0,
        haul_mile_rate: 0,
        haul_mile: 0,
        min_mile: 0,
      },
      jobData: {
        date: '',
        customer_id: {},
        producer_id: {},
        crop_id: {},
        field_id: {},
        area_rate_id: {},
        bag_pit_id: {},
        moisture: 0
      },
      customerRateData: {
        at_moisture: 0,
        rate: 0,
      },
      producerRateData: {
        moisture: 0,
        rate: 0,
      },
      producerAreaRateData: {
        name: '',
        ton_rate: 0,
        mile_rate: 0,
        min_miles: 0,
      },
      mileData: {
        customer_id: {},
        producer_id: {},
        field_id: {},
        miles: 0
      },
      trucks: [],
      haulers: [],
      customerBagPits: [],
      recentTicketNumbers: [],
      duplicateTicketWarningShownFor: null,
    };
  },
  computed: {
    bagPitOptions() {
      if (!this.localFormData.customer_id) {
        return [];
      }

      return this.customerBagPits
        .filter(bagPit => bagPit.customer_id?.id === this.localFormData.customer_id)
        .map(bagPit => ({
          label: bagPit.name,
          value: bagPit.id,
        }));
    },
    netWeight() {
      return parseFloat(
        ((this.localFormData.gross_weight - this.localFormData.tare_weight) / 2000).toFixed(2)
      );
    },
    adjustedWeight() {
      const multiplier =
        (100 - this.localFormData.moisture) / (100 - this.localFormData.at_moisture);
      return parseFloat((multiplier * this.netWeight).toFixed(2));
    },
    baseHaulCost() {
      return parseFloat(
        (this.netWeight * this.localFormData.haul_rate).toFixed(2)
      );
    },
    extraMileHaulCost() {
      const extraMiles = Math.max(
        this.localFormData.haul_mile - this.localFormData.min_mile,
        0
      );
      return parseFloat(
        (extraMiles * this.netWeight * this.localFormData.haul_mile_rate).toFixed(2)
      );
    },
    totalHaulCost() {
      return parseFloat(
        (this.baseHaulCost + this.extraMileHaulCost).toFixed(2)
      );
    },
    producerGrossPay() {
      return parseFloat(
        (this.adjustedWeight * this.localFormData.producer_rate).toFixed(2)
      );
    },
    producerNetPay() {
      return parseFloat(
        (this.producerGrossPay - this.extraMileHaulCost).toFixed(2)
      );
    },
    customerPay() {
      return parseFloat(
        (this.adjustedWeight * this.localFormData.customer_rate).toFixed(2)
      );
    },
  },
  created() {
    this.loadJobData();
    this.loadCustomerBagPits();
    this.loadTrucks();
    this.loadRecentTickets();
  },
  methods: {
    loadCustomerBagPits() {
      axios.get('/api/v1/customer_bag_pit/customer_bag_pits/').then(response => {
        this.customerBagPits = response.data;
      }).catch(error => {
        console.log(error.status);
      });
    },
    loadJobData() {
      axios.get(`/api/v1/job/jobs/${this.jobId}/`).then(response => {
        this.jobData = { ...response.data }

        this.localFormData.date = this.jobData.date;
        this.localFormData.moisture = this.jobData.moisture;
        this.localFormData.job_id = this.jobData.id;
        this.localFormData.bag_pit_id = this.jobData.bag_pit_id?.id || null;
        this.localFormData.producer_id = this.jobData.producer_id.id;
        this.localFormData.customer_id = this.jobData.customer_id.id;

        // load additional information depending on job data
        this.loadCustomerRateData();
        this.loadProducerRateData();
        this.loadProducerAreaRateData();
        this.loadMileData();

      }).catch(error => {
        console.log(error.status);
      });
    },
    loadRecentTickets() {
      axios.get(`/api/v1/ticket_crop/ticket-crop/recent-numbers/`).then(response => {
        this.recentTicketNumbers = response.data;
      }).catch(error => {
        console.log(error.status);
      });
    },
    loadMileData() {
      const prodId = this.jobData.producer_id.id;
      const custId = this.jobData.customer_id.id;
      const fieldId = this.jobData.field_id.id;

      let url = `/api/v1/mile/miles/?prod_id=${prodId}&cust_id=${custId}`;
      
      if (fieldId) {
        url += `&field_id=${fieldId}`
      }

      axios.get(url).then(response => {
        if (response.data && response.data.length > 0) {
          this.mileData = { ...response.data[0] }

          //update form data with intial values
          this.localFormData.haul_mile = this.mileData.miles;

        } else {
          this.$q.notify({ color: 'red', message: `Cannot locate miles for Producer Crop and Field.`, icon: 'error' });
        }
      }).catch(error => {
        console.log(error)
      });
    },
    loadProducerAreaRateData() {
      const prodId = this.jobData.producer_id.id;
      const cropId = this.jobData.crop_id.id;

      const url = `/api/v1/producer_area_rate/producer-area-rates/?producer_id=${prodId}&crop_id=${cropId}`;

      axios.get(url).then(response => {
        if (response.data && response.data.length > 0) {
          this.producerAreaRateData = { ...response.data[0] }

          //update form data with intial values
          this.localFormData.haul_rate = this.producerAreaRateData.ton_rate;
          this.localFormData.haul_mile_rate = this.producerAreaRateData.mile_rate;
          this.localFormData.min_mile = this.producerAreaRateData.min_miles;

        } else {
          this.$q.notify({ color: 'red', message: `Cannot locate Producer Area Rate for ${this.jobData.crop_id.crop}.`, icon: 'error' });
        }
      }).catch(error => {
        console.log(error)
      });
    },
    loadCustomerRateData() {
      const custId = this.jobData.customer_id.id;
      const cropId = this.jobData.crop_id.id;

      const url = `/api/v1/customer_rate/customer_rates/?cust_id=${custId}&crop_id=${cropId}`;

      axios.get(url).then(response => {
        if (response.data && response.data.length > 0) {
          this.customerRateData = { ...response.data[0] }

          // update form data with intial values
          this.localFormData.customer_rate = this.customerRateData.rate;
          this.localFormData.at_moisture = this.customerRateData.at_moisture;

        } else {
          this.$q.notify({ color: 'red', message: `Cannot locate rate for ${this.jobData.customer_id.name} / ${this.jobData.crop_id.crop}.`, icon: 'error' });
        }
      }).catch(error => {
        console.log(error)
      });
    },
    loadProducerRateData() {
      const prodId = this.jobData.producer_id.id;
      const cropId = this.jobData.crop_id.id;

      const url = `/api/v1/producer_rate/producer_rates/?prod_id=${prodId}&crop_id=${cropId}`;

      axios.get(url).then(response => {
        if (response.data && response.data.length > 0) {
          this.producerRateData = { ...response.data[0] }

          // update form data with intial values
          this.localFormData.producer_rate = this.producerRateData.rate;
          //this.localFormData.customer_rate = this.customerRateData.rate;

        } else {
          this.$q.notify({ color: 'red', message: `Cannot locate producer rate for ${this.jobData.producer_id.name} / ${this.jobData.crop_id.crop}.`, icon: 'error' });
        }
      }).catch(error => {
        console.log(error)
      });
    },
    loadTrucks() {
      axios.get(`/api/v1/truck/trucks/`).then(response => {
        // Sort trucks alphabetically by name and map to label-value pairs for `q-select`
        this.trucks = response.data
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(truck => ({
          label: truck.name,
          value: truck.id,
          hauler: truck.hauler_id
      }));
      }).catch(error => {
        console.log(error.status);
      });
    },
    handleFormSubmit() {
      axios.post('/api/v1/ticket_crop/ticket-crop/', this.localFormData).then(() => {
        this.$q.notify({ color: 'green', message: 'Ticket submitted successfully!', icon: 'check' });
        this.recentTicketNumbers.push(this.localFormData.ticket_number);
        this.setupNextTicket();
      })
      .catch(error => {
        if (error.response && error.response.data) {
          const errorMessages = Object.values(error.response.data)
            .flat()  // Flatten nested arrays (e.g., field-specific errors)
            .join('; '); // Concatenate messages into one string

          this.$q.notify({ color: 'red', message: `Failed to submit ticket: ${errorMessages}`, icon: 'error' });
        } else {
          this.$q.notify({ color: 'red', message: 'Failed to submit ticket.', icon: 'error' });
        }
        console.error(error);
      });
    },
    setupNextTicket() {
      // clear ticket data and focus on first input
      this.localFormData.ticket_number = '';
      this.localFormData.truck_id = null;
      this.localFormData.gross_weight = '';
      this.localFormData.tare_weight = '';
      this.localFormData.hauler_id = '';
      this.duplicateTicketWarningShownFor = null;

      this.$refs.ticketNumber.focus();
    },
    viewAllJobTickets() {
      if (this.jobId) {
        this.$router.push({ path: '/ticket_crop/list', query: { job_id: this.jobId } });
      }
    },
    handleReset() {
      this.localFormData = this.setupNextTicket();
    },
    handleEnterKey(refKey) {
      // advance to next form field on enter/select
      const fieldOrder = [
        'ticketNumber',
        'truckId',
        'grossWeight',
        'tareWeight',
        // Add more ref keys here in order
      ];

      const currentIndex = fieldOrder.indexOf(refKey);
      if (currentIndex === -1) return;

      const nextRefKey = fieldOrder[currentIndex + 1];

      if (!nextRefKey) {
        // Focus the submit button if this is the last field
        this.$refs.submitBtn?.$el.focus?.();
        return;
      }

      let next = this.$refs[nextRefKey];
      if (Array.isArray(next)) next = next[0];

      if (!next) return;

      if (typeof next.focus === 'function') {
        next.focus();
      } else if (next.$el?.focus) {
        next.$el.focus();
      }

      // If q-select or date, optionally open popup
      if (typeof next.showPopup === 'function') {
        setTimeout(() => next.showPopup(), 100);
      }
    },

    onSelectChanged(refKey) {
      // Wait for popup to close before focusing next
      setTimeout(() => {
        this.handleEnterKey(refKey);
      }, 100);
    },
    checkDuplicateTicketNumber() {
      const ticketNumber = this.localFormData.ticket_number;
      if (!ticketNumber || !this.recentTicketNumbers.includes(ticketNumber)) {
        this.duplicateTicketWarningShownFor = null;
        return;
      }

      if (this.duplicateTicketWarningShownFor === ticketNumber) {
        return;
      }

      this.duplicateTicketWarningShownFor = ticketNumber;
      window.alert(`Duplicate Ticket Number\n\nTicket number ${ticketNumber} has been recently entered.`);
    },
  },
  watch: {
    'localFormData.truck_id': function (selectedTruckId) {
      // Find the selected truck's hauler and update the hauler selection
      const selectedTruck = this.trucks.find(truck => truck.value === selectedTruckId);
      if (selectedTruck) {
        this.localFormData.hauler_id = selectedTruck.hauler.id;
        this.haulers = [{ label: selectedTruck.hauler.name, value: selectedTruck.hauler.id }]; // Set hauler options dynamically
      } else {
        this.localFormData.hauler = null;
        this.haulers = [];
      }
    },
  },
};
</script>

<style scoped>
.form-holder {
  max-width: 1200px;
  margin: auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-left {
}

.flex-row {
  display: flex;
  gap: 1rem;
}

.button-container {
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 1.5rem;
}

.info-heading {
  font-size: 1.25em;
}

.info-item {
  border-radius: 3px;
  padding: .5em;
}
</style>
